import jsPDF from 'jspdf';
import { APP_NAME, APP_SUBTITLE } from '../data/knowledgeBase';
import type { PreparationResult } from '../types';
import yaraKnowledgeGrows from '../assets/yara-knowledge-grows.png';

const palette = {
  forest: '#003DA6',
  forestSoft: '#1D58C7',
  graphite: '#102241',
  slate: '#3F5888',
  line: '#C3D3F3',
  paper: '#EDF3FF',
  white: '#FFFFFF'
};

const COPYRIGHT_LINE_1 = '© 2026 Yara Brasil | Paixão por Vendas | José Ricardo Noronha®';
const COPYRIGHT_LINE_2 = 'Inteligência aplicada à Venda de Valor no Agro | Todos os direitos reservados.';

const A4_HEIGHT = 297;
const CONTENT_BOTTOM = 280;

const slugify = (value: string): string =>
  value
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeListItem = (item: string): string =>
  item.replace(/\s*\n+\s*/g, ' ').replace(/\s+/g, ' ').trim();

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Não foi possível carregar a imagem: ${src}`));
    img.src = src;
  });

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  };
};

const interpolateHex = (from: string, to: string, ratio: number): { r: number; g: number; b: number } => {
  const start = hexToRgb(from);
  const end = hexToRgb(to);

  return {
    r: Math.round(start.r + (end.r - start.r) * ratio),
    g: Math.round(start.g + (end.g - start.g) * ratio),
    b: Math.round(start.b + (end.b - start.b) * ratio)
  };
};

const drawHorizontalGradient = (
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  from: string,
  to: string,
  steps = 42
): void => {
  const bandWidth = width / steps;

  for (let index = 0; index < steps; index += 1) {
    const ratio = index / Math.max(steps - 1, 1);
    const color = interpolateHex(from, to, ratio);
    doc.setFillColor(color.r, color.g, color.b);
    doc.rect(x + index * bandWidth, y, bandWidth + 0.3, height, 'F');
  }
};

const paintPageBackground = (doc: jsPDF): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(244, 248, 255);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
};

const fitLines = (doc: jsPDF, text: string, width: number, maxLines: number): string[] => {
  const lines = doc.splitTextToSize(normalizeListItem(text), width) as string[];
  if (lines.length <= maxLines) {
    return lines;
  }

  const trimmed = lines.slice(0, maxLines);
  const last = trimmed[maxLines - 1] ?? '';
  trimmed[maxLines - 1] = last.length > 3 ? `${last.slice(0, Math.max(last.length - 3, 0))}...` : `${last}...`;
  return trimmed;
};

const drawTagPill = (doc: jsPDF, label: string, value: string, x: number, y: number, w: number): void => {
  doc.setFillColor(234, 241, 255);
  doc.setDrawColor(198, 215, 246);
  doc.roundedRect(x, y, w, 15, 3, 3, 'FD');

  doc.setTextColor(palette.slate);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.4);
  doc.text(label.toUpperCase(), x + 3, y + 4.7);

  doc.setTextColor(palette.graphite);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.1);
  const valueLines = fitLines(doc, value, w - 6, 2);
  doc.text(valueLines, x + 3, y + 9.2);
};

const drawRadarBar = (doc: jsPDF, label: string, value: number, x: number, y: number, w: number): void => {
  doc.setTextColor(palette.graphite);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.9);
  doc.text(label, x, y);
  doc.setFont('helvetica', 'bold');
  doc.text(String(value), x + w, y, { align: 'right' });

  doc.setFillColor(219, 231, 251);
  doc.roundedRect(x, y + 2.1, w, 3.8, 2, 2, 'F');
  doc.setFillColor(0, 61, 166);
  doc.roundedRect(x, y + 2.1, (w * Math.max(0, Math.min(100, value))) / 100, 3.8, 2, 2, 'F');
};

const drawScenarioOverviewCard = (doc: jsPDF, result: PreparationResult, cursorY: number): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const cardX = 12;
  const cardY = cursorY;
  const cardW = pageWidth - 24;
  const cardH = 100;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(195, 211, 243);
  doc.setLineWidth(0.4);
  doc.roundedRect(cardX, cardY, cardW, cardH, 5, 5, 'FD');

  doc.setTextColor(palette.forest);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.8);
  doc.text('Contexto da reunião', cardX + 5, cardY + 7.5);

  doc.setTextColor(palette.graphite);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.7);
  const scenarioLines = fitLines(doc, result.cenarioOriginal, cardW - 10, 4);
  doc.text(scenarioLines, cardX + 5, cardY + 13.5);

  const pillY = cardY + 28.5;
  const gap = 3;
  const pillW = (cardW - 10 - gap * 2) / 3;
  drawTagPill(doc, 'Perfil do produtor', translateBehaviorProfile(result.producerProfile), cardX + 5, pillY, pillW);
  drawTagPill(doc, 'Classificação', translateClientPrioritySegment(result.clientPrioritySegment), cardX + 5 + pillW + gap, pillY, pillW);
  drawTagPill(doc, 'Cultura', translateSelectedCulture(result.selectedCulture), cardX + 5 + (pillW + gap) * 2, pillY, pillW);

  doc.setTextColor(palette.forest);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.6);
  doc.text('Radar da conversa', cardX + 5, cardY + 50);
  const radarX = cardX + 5;
  const radarW = cardW - 10;
  let radarY = cardY + 55;
  drawRadarBar(doc, 'Preço', result.conversationRadar.pressaoPorPreco, radarX, radarY, radarW);
  radarY += 8.8;
  drawRadarBar(doc, 'Concorrência', result.conversationRadar.riscoConcorrencial, radarX, radarY, radarW);
  radarY += 8.8;
  drawRadarBar(doc, 'Potencial', result.conversationRadar.potencialDaConta, radarX, radarY, radarW);
  radarY += 8.8;
  drawRadarBar(doc, 'Resistência', result.conversationRadar.nivelDeResistencia, radarX, radarY, radarW);
  radarY += 8.8;
  drawRadarBar(doc, 'Premium', result.conversationRadar.aberturaAoPremium, radarX, radarY, radarW);

  return cardY + cardH + 6;
};

const drawMiniListCard = (
  doc: jsPDF,
  title: string,
  items: string[],
  x: number,
  y: number,
  w: number,
  h: number,
  accent = '#EAF1FF',
  maxLinesPerItem = 3
): void => {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(195, 211, 243);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 4, 4, 'FD');

  const { r, g, b } = hexToRgb(accent);
  doc.setFillColor(r, g, b);
  doc.roundedRect(x + 1.5, y + 1.5, w - 3, 7, 2.5, 2.5, 'F');

  doc.setTextColor(palette.forest);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.8);
  doc.text(title, x + 4, y + 6.2);

  let textY = y + 12;
  const safeItems = items.map(normalizeListItem).filter(Boolean);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.4);
  doc.setTextColor(palette.graphite);

  safeItems.forEach((item) => {
    if (textY > y + h - 5) {
      return;
    }

    const itemLines = fitLines(doc, `• ${item}`, w - 8, maxLinesPerItem);
    doc.text(itemLines, x + 4, textY);
    textY += itemLines.length * 3.6 + 1;
  });
};

const drawSectionShell = (doc: jsPDF, title: string, x: number, y: number, w: number, h: number): void => {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(195, 211, 243);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, w, h, 5, 5, 'FD');

  doc.setFillColor(23, 59, 154);
  doc.roundedRect(x + 4, y + 3, w - 8, 8, 3.5, 3.5, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.6);
  doc.text(title, x + 8, y + 8.2);
};

const drawThreeColumnSection = (
  doc: jsPDF,
  title: string,
  columns: Array<{ heading: string; items: string[]; accent?: string }>,
  y: number,
  options?: { cardHeight?: number; maxLinesPerItem?: number }
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const shellX = 12;
  const shellW = pageWidth - 24;
  const cardH = options?.cardHeight ?? 58;
  const maxLinesPerItem = options?.maxLinesPerItem ?? 3;
  const shellH = 18 + cardH + 8;
  const innerY = y + 14;
  const gap = 3.5;
  const colW = (shellW - 10 - gap * 2) / 3;

  drawSectionShell(doc, title, shellX, y, shellW, shellH);

  columns.forEach((column, index) => {
    drawMiniListCard(
      doc,
      column.heading,
      column.items,
      shellX + 5 + index * (colW + gap),
      innerY,
      colW,
      cardH,
      column.accent ?? '#EAF1FF',
      maxLinesPerItem
    );
  });

  return y + shellH + 6;
};

const drawGridSection = (
  doc: jsPDF,
  title: string,
  cards: Array<{ heading: string; items: string[]; accent?: string }>,
  y: number,
  options?: { rows?: number; cardHeight?: number; maxLinesPerItem?: number }
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const shellX = 12;
  const shellW = pageWidth - 24;
  const gap = 4;
  const cardW = (shellW - 10 - gap) / 2;
  const rows = options?.rows ?? 2;
  const cardH = options?.cardHeight ?? 54;
  const maxLinesPerItem = options?.maxLinesPerItem ?? 3;
  const shellH = 18 + rows * cardH + (rows - 1) * gap + 6;
  const startY = y + 14;

  drawSectionShell(doc, title, shellX, y, shellW, shellH);

  cards.forEach((card, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    drawMiniListCard(
      doc,
      card.heading,
      card.items,
      shellX + 5 + column * (cardW + gap),
      startY + row * (cardH + gap),
      cardW,
      cardH,
      card.accent ?? '#EAF1FF',
      maxLinesPerItem
    );
  });

  return y + shellH + 6;
};

const translateBehaviorProfile = (profile: PreparationResult['producerProfile']): string => {
  const map: Record<PreparationResult['producerProfile'], string> = {
    analitico: 'Analítico',
    diretivo: 'Diretivo',
    integrador: 'Integrador',
    expressivo: 'Expressivo'
  };

  return map[profile];
};

const translateClientPrioritySegment = (segment: PreparationResult['clientPrioritySegment']): string => {
  const map: Record<PreparationResult['clientPrioritySegment'], string> = {
    avaliar: 'Avaliar',
    conquistar: 'Conquistar',
    incentivar: 'Incentivar',
    blindar: 'Blindar'
  };

  return map[segment];
};

const translateSelectedCulture = (culture: PreparationResult['selectedCulture']): string => {
  const map: Record<PreparationResult['selectedCulture'], string> = {
    soja: 'Soja',
    milho: 'Milho',
    trigo: 'Trigo',
    cevada: 'Cevada',
    cafe: 'Café',
    citros: 'Citros',
    uva: 'Uva',
    banana: 'Banana',
    hortifruti: 'Hortifruti',
    tomate: 'Tomate',
    batata: 'Batata',
    cebola: 'Cebola',
    cana: 'Cana-de-açúcar',
    algodao: 'Algodão',
    feijao: 'Feijão',
    arroz: 'Arroz',
    sorgo: 'Sorgo',
    pastagem: 'Pastagem',
    frutas: 'Frutas',
    vegetais: 'Vegetais',
    pecuaria: 'Pecuária',
    outra: 'Outra',
    geral: 'Geral'
  };

  return map[culture];
};

const addPageHeader = (doc: jsPDF, _generatedAt: string, logo?: HTMLImageElement): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  paintPageBackground(doc);

  drawHorizontalGradient(doc, 0, 0, pageWidth, 26, '#003086', '#6e97e8', 32);

  doc.setTextColor(palette.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(APP_NAME, 16, 11.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.8);
  doc.text('Preparação executiva de conversas de valor', 16, 18);

  if (logo) {
    doc.addImage(logo, 'PNG', pageWidth - 43, 4.4, 27, 17.5);
  }
};

const addPageFooter = (doc: jsPDF, generatedAt: string): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const totalPages = doc.getNumberOfPages();

  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(palette.line);
    doc.line(16, A4_HEIGHT - 17, pageWidth - 16, A4_HEIGHT - 17);

    doc.setTextColor(palette.graphite);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.text(`Gerado em ${generatedAt}`, 16, A4_HEIGHT - 13.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    doc.text(COPYRIGHT_LINE_1, 16, A4_HEIGHT - 9.1);
    doc.text(COPYRIGHT_LINE_2, 16, A4_HEIGHT - 5.4);
    doc.text(`Página ${page}/${totalPages}`, pageWidth - 16, A4_HEIGHT - 5.4, { align: 'right' });
  }
};

const ensureSpace = (doc: jsPDF, cursorY: number, needed: number, generatedAt: string, logo?: HTMLImageElement): number => {
  if (cursorY + needed <= CONTENT_BOTTOM) {
    return cursorY;
  }

  doc.addPage();
  addPageHeader(doc, generatedAt, logo);
  return 34;
};

const drawImportantNote = (doc: jsPDF, cursorY: number, generatedAt: string, logo?: HTMLImageElement): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const boxX = 16;
  const boxW = pageWidth - 32;
  const textX = boxX + 4;
  const paragraphTopGap = 8.5;
  const lineHeight = 4.4;
  const bottomPadding = 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.4);
  const noteLines = doc.splitTextToSize(
    'Este material foi gerado com apoio de Inteligência Artificial e pode conter imprecisões. Serve como apoio à preparação, mas não substitui o estudo individual nem a participação ativa nos Treinamentos e Simuladas de Venda de Valor.',
    boxW - 8
  );
  const boxH = paragraphTopGap + noteLines.length * lineHeight + bottomPadding;
  let nextY = ensureSpace(doc, cursorY + 2, boxH + 2, generatedAt, logo);

  doc.setFillColor(palette.paper);
  doc.setDrawColor(palette.forest);
  doc.setLineWidth(0.35);
  doc.roundedRect(boxX, nextY, boxW, boxH, 2, 2, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  doc.setTextColor(palette.graphite);
  doc.text(noteLines.join(' '), textX, nextY + paragraphTopGap, {
    maxWidth: boxW - 8,
    align: 'justify'
  });

  return nextY + boxH + 2;
};

const drawValueSineChart = (doc: jsPDF, result: PreparationResult, generatedAt: string, logo?: HTMLImageElement): void => {
  doc.addPage();
  addPageHeader(doc, generatedAt, logo);

  const pageWidth = doc.internal.pageSize.getWidth();
  const cardX = 12;
  const cardY = 30;
  const cardW = pageWidth - 24;
  const cardH = 245;
  const left = 28;
  const right = pageWidth - 24;
  const chartTop = 72;
  const chartBottom = 160;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(195, 211, 243);
  doc.setLineWidth(0.35);
  doc.roundedRect(cardX, cardY, cardW, cardH, 5, 5, 'FD');

  doc.setFillColor(23, 59, 154);
  doc.roundedRect(cardX + 4, cardY + 3, cardW - 8, 8, 3.5, 3.5, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.3);
  doc.text('5. Senoide | Narrativa da conversa', cardX + 8, cardY + 8.2);

  doc.setTextColor(palette.forest);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(result.valueSineMap.titulo, left, 47);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.7);
  doc.setTextColor(palette.slate);
  const subtitle = doc.splitTextToSize(result.valueSineMap.subtitulo, pageWidth - 52);
  doc.text(subtitle, left, 53);

  doc.setDrawColor('#1F4DAA');
  doc.setLineWidth(0.4);
  doc.line(left, chartTop, left, chartBottom);

  doc.setTextColor(palette.graphite);
  doc.setFontSize(8.6);
  doc.text('Positivo', cardX + 4, chartTop + 2);
  doc.text('Neutro', cardX + 4, (chartTop + chartBottom) / 2 + 1);
  doc.text('Negativo', cardX + 4, chartBottom - 1);

  doc.setDrawColor('#9FB6E4');
  doc.setLineWidth(0.2);
  doc.setLineDashPattern([1.2, 1.2], 0);
  doc.line(left, (chartTop + chartBottom) / 2, right, (chartTop + chartBottom) / 2);
  doc.setLineDashPattern([], 0);

  const points = [
    { x: 48, y: 123 },
    { x: 70, y: 101 },
    { x: 92, y: 124 },
    { x: 114, y: 134 },
    { x: 136, y: 118 },
    { x: 158, y: 101 }
  ];

  doc.setDrawColor('#1F4DAA');
  doc.setLineWidth(0.45);
  doc.lines(
    [
      [8, -14, 15, -24, 22, -22],
      [8, 5, 15, 15, 22, 23],
      [8, 5, 15, 9, 22, 10],
      [8, -1, 15, -11, 22, -16],
      [8, -3, 15, -10, 22, -17]
    ],
    points[0].x,
    points[0].y
  );

  doc.setFillColor('#003DA6');
  points.forEach((point, index) => {
    doc.circle(point.x, point.y, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor('#0F2F73');
    doc.text(String(index + 1), point.x, point.y - 3.2, { align: 'center' });
  });

  const colors = ['#DDEEB6', '#DDEEB6', '#FFE08A', '#FFE08A', '#BEE5FB', '#BEE5FB'];
  const boxW = 22;
  const boxH = 18;
  const boxTopByStep = [112, 92, 116, 130, 112, 92];

  points.forEach((point, index) => {
    const boxX = point.x - boxW / 2;
    const boxY = boxTopByStep[index];
    const step = result.valueSineMap.steps[index];
    doc.setFillColor(colors[index]);
    doc.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'F');
    doc.setDrawColor('#b5c8bb');
    doc.roundedRect(boxX, boxY, boxW, boxH, 2, 2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.6);
    doc.setTextColor('#0F2F73');
    doc.text(doc.splitTextToSize(step.titulo, boxW - 3), boxX + 1.5, boxY + 4.5);
  });

  const phaseY = 168;
  const phaseW = (right - left - 4) / 3;
  const phases = [
    { name: 'Gancho', color: '#c8e88b' },
    { name: 'Impacto', color: '#ffd05a' },
    { name: 'Solução', color: '#6ccbf7' }
  ];

  phases.forEach((phase, index) => {
    const x = left + index * (phaseW + 2);
    doc.setFillColor(phase.color);
    doc.roundedRect(x, phaseY, phaseW, 10, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(palette.graphite);
    doc.text(phase.name, x + phaseW / 2, phaseY + 6.6, { align: 'center' });
  });

  let y = 182;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.8);
  doc.setTextColor(palette.forest);
  doc.text('Como conduzir cada etapa', left, y);
  y += 5.5;

  const miniCardGap = 3.5;
  const miniCardW = (pageWidth - 24 - 7) / 2;
  const miniCardXPositions = [12, 12 + miniCardW + miniCardGap];
  const miniCardHeights = [22, 22, 22, 22, 28, 28];
  const miniCardYs = [y, y, y + 24.5, y + 24.5, y + 49, y + 49];

  result.valueSineMap.steps.forEach((step, index) => {
    const boxX = miniCardXPositions[index % 2];
    const boxY = miniCardYs[index];
    const stepColor = colors[index];

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(195, 211, 243);
    doc.roundedRect(boxX, boxY, miniCardW, miniCardHeights[index], 3, 3, 'FD');

    doc.setFillColor(stepColor);
    doc.roundedRect(boxX + 1.5, boxY + 1.5, miniCardW - 3, 5.5, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.3);
    doc.setTextColor('#0F2F73');
    doc.text(`${step.id}. ${step.titulo}`, boxX + 4, boxY + 5.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(palette.graphite);
    const suggestion = doc.splitTextToSize(step.sugestaoPersonalizada, miniCardW - 8);
    doc.text(suggestion.slice(0, index >= 4 ? 5 : 4), boxX + 4, boxY + 10.5);
  });

  drawImportantNote(doc, 271, generatedAt, logo);
};

const drawExecutiveHero = (
  doc: jsPDF,
  _generatedAt: string,
  logo?: HTMLImageElement
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const boxX = 12;
  const boxY = 14;
  const boxW = pageWidth - 24;
  const boxH = 43;

  doc.setFillColor(23, 59, 154);
  doc.roundedRect(boxX, boxY, boxW, boxH, 6, 6, 'F');
  drawHorizontalGradient(doc, boxX, boxY, boxW, boxH, '#173b9a', '#7ea5ef', 44);

  doc.setTextColor(palette.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18.5);
  doc.text(APP_NAME, boxX + 6, boxY + 10.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.2);
  doc.text(APP_SUBTITLE, boxX + 6, boxY + 17.3);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.6);
  const description = doc.splitTextToSize(
    'Prepare conversas de valor com mais clareza, consistência e impacto, levando a decisão para produtividade, rentabilidade, risco e retorno por hectare.',
    boxW - 62
  );
  doc.text(description, boxX + 6, boxY + 23.6);

  const chips = [
    'OPC | Direção estratégica',
    'SPIN | Diagnóstico consultivo',
    'EPA | Provocação de valor',
    'Senoide | Narrativa de venda'
  ];
  let chipX = boxX + 6;
  const chipY = boxY + 34;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);

  chips.forEach((chip) => {
    const chipW = doc.getTextWidth(chip) + 6.5;
    doc.setFillColor(58, 96, 186);
    doc.roundedRect(chipX, chipY, chipW, 6.4, 1.8, 1.8, 'F');
    doc.setDrawColor(215, 229, 255);
    doc.setLineWidth(0.15);
    doc.roundedRect(chipX, chipY, chipW, 6.4, 1.8, 1.8);
    doc.setTextColor(palette.white);
    doc.text(chip, chipX + 3.2, chipY + 4.3);
    chipX += chipW + 2.2;
  });

  if (logo) {
    doc.addImage(logo, 'PNG', pageWidth - 45, boxY + 5, 28, 22);
  }

  return boxY + boxH + 7;
};

export const exportPreparationToPdf = async (result: PreparationResult): Promise<void> => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logo = await loadImage(yaraKnowledgeGrows);
  const generatedAt = new Date(result.metadata.generatedAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  paintPageBackground(doc);

  let cursorY = drawExecutiveHero(doc, generatedAt, logo);
  cursorY = drawScenarioOverviewCard(doc, result, cursorY);
  drawThreeColumnSection(
    doc,
    '1. Orientação Comportamental',
    [
      { heading: 'Linguagem recomendada', items: result.behavioralGuidance.linguagemRecomendada, accent: '#DBE8FF' },
      { heading: 'Postura recomendada', items: result.behavioralGuidance.posturaRecomendada, accent: '#E6EEFF' },
      { heading: 'Ritmo e fechamento', items: [...result.behavioralGuidance.ritmoDaConversa, ...result.behavioralGuidance.formaDeFechamento], accent: '#DCEBFF' }
    ],
    cursorY,
    { cardHeight: 60, maxLinesPerItem: 4 }
  );

  doc.addPage();
  addPageHeader(doc, generatedAt, logo);
  cursorY = 34;
  cursorY = drawThreeColumnSection(
    doc,
    '2. OPC | Estratégia da Conversa',
    [
      { heading: 'Objetivos sugeridos', items: result.opc.objetivo, accent: '#DBE8FF' },
      { heading: 'Processo de condução', items: result.opc.processo, accent: '#E6EEFF' },
      { heading: 'Compromissos sugeridos', items: result.opc.compromisso, accent: '#DCEBFF' }
    ],
    cursorY,
    { cardHeight: 72, maxLinesPerItem: 6 }
  );

  doc.addPage();
  addPageHeader(doc, generatedAt, logo);
  cursorY = 34;
  cursorY = drawGridSection(
    doc,
    '3. SPIN | Perguntas que movem a decisão',
    [
      { heading: 'Situação', items: result.spin.situacao, accent: '#E2EDFF' },
      { heading: 'Problema', items: result.spin.problema, accent: '#E9F1FF' },
      { heading: 'Implicação', items: result.spin.implicacao, accent: '#E2EDFF' },
      { heading: 'Necessidade de solução', items: result.spin.necessidadeSolucao, accent: '#E9F1FF' }
    ],
    cursorY,
    { rows: 2, cardHeight: 68, maxLinesPerItem: 5 }
  );

  doc.addPage();
  addPageHeader(doc, generatedAt, logo);
  cursorY = 34;
  drawGridSection(
    doc,
    '4. EPA | Provocações de Valor',
    [
      { heading: 'Educar', items: result.epa.educar, accent: '#E2EDFF' },
      { heading: 'Personalizar', items: result.epa.personalizar, accent: '#E9F1FF' },
      { heading: 'Assumir o controle', items: result.epa.assumirControle, accent: '#E2EDFF' }
    ],
    cursorY,
    { rows: 2, cardHeight: 68, maxLinesPerItem: 6 }
  );

  drawValueSineChart(doc, result, generatedAt, logo);

  addPageFooter(doc, generatedAt);

  const filename = `ficha-${slugify(APP_NAME)}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
};
